import VueWrapper from '@/components/core/Vue/vue.wrapper';
import {
    ArcRotateCamera,
    Color3,
    Color4,
    Engine,
    HemisphericLight,
    HighlightLayer,
    Mesh,
    Node,
    Scene,
    SceneLoader,
    TransformNode,
    Vector3,
} from '@babylonjs/core';
// import { Inspector } from '@babylonjs/inspector';  // Removed Inspector import
import '@babylonjs/loaders';
import {Component, Prop} from 'vue-property-decorator';

@Component({
    name: 'DigitalTwinCanvasComponent',
    components: {},
})
export default class DigitalTwinCanvasComponent extends VueWrapper {
    private engine!: Engine;
    private scene!: Scene;
    private highlightLayer!: HighlightLayer;

    @Prop({
        type: String,
        required: true,
    })
    public siteLocationId!: string;

    /**
     * The EXACT node name for the equipment to highlight.
     */
    private readonly EQUIPMENT_NODE_NAME: string = this.siteLocationId;

    /**
     * The EXACT node name for the camera object that defines the desired view.
     */
    private readonly CAMERA_NODE_NAME: string = 'Camera_ ' + this.siteLocationId;

    private selectedNode: Node | null = null;
    public othersVisible: boolean = true;

    // NEW: Loading indicator property.
    public loading: boolean = true;

    public $refs!: {
        bjsCanvas: HTMLCanvasElement;
    };

    public mounted() {
        this.initializeBabylonJS();
    }

    beforeDestroy() {
        if (this.engine) {
            this.engine.dispose();
        }
    }

    private async initializeBabylonJS() {
        const canvas = this.$refs.bjsCanvas;
        if (canvas) {
            canvas.addEventListener(
                'wheel',
                e => {
                    e.preventDefault();
                },
                {passive: false}
            );
        }
        if (!canvas) {
            console.error('Canvas element not found');
            this.loading = false;
            return;
        }
        this.engine = new Engine(canvas, true);
        this.scene = new Scene(this.engine);
        this.scene.clearColor = new Color4(0, 0, 0, 0.76);

        const light = new HemisphericLight('defaultLight', new Vector3(0, 1, 0), this.scene);
        light.intensity = 1.0;

        try {
            // Load model from /public/models/
            await SceneLoader.ImportMeshAsync(
                '',
                `${this.ApiUrl}/documentations/digital-twin-model-files/`,
                'purifier-room-3D-model-equipment-ID_w_tag_test3.glb',
                this.scene,
                undefined,
                '.glb'
            );
            this.highlightLayer = new HighlightLayer('highlightLayer', this.scene);

            // Find the equipment node.
            const equipmentNode = this.scene.getNodeByName(this.EQUIPMENT_NODE_NAME);
            if (equipmentNode) {
                console.log('Found equipment node:', equipmentNode.name);
                this.selectedNode = equipmentNode;
                if (equipmentNode instanceof Mesh) {
                    this.highlightLayer.addMesh(equipmentNode, Color3.Yellow());
                }
                // BUGGED RIGHT NOW, NEED TO FIX
            } else {
                console.warn(`Equipment node "${this.EQUIPMENT_NODE_NAME}" not found!`);
            }

            // Determine target position for the camera.
            let targetPos = Vector3.Zero();
            if (this.selectedNode) {
                if (this.selectedNode instanceof Mesh) {
                    targetPos = this.selectedNode.getBoundingInfo().boundingBox.centerWorld.clone();
                } else if (this.selectedNode instanceof TransformNode) {
                    targetPos = this.selectedNode.getAbsolutePosition().clone();
                }
            }
            // Find and use the camera node.
            const camNode = this.scene.getNodeByName(this.CAMERA_NODE_NAME);
            if (camNode && camNode instanceof TransformNode) {
                console.log('Found camera node:', camNode.name);
                this.useCameraNodeAsArc(camNode, targetPos);
            } else {
                console.warn(`Camera node "${this.CAMERA_NODE_NAME}" not found or not a TransformNode.`);
                // Fallback camera.
                const fallbackCam = new ArcRotateCamera('fallbackCam', Math.PI / 2, Math.PI / 2.5, 10, targetPos, this.scene);
                fallbackCam.attachControl(canvas, true);
                this.scene.activeCamera = fallbackCam;
            }
        } catch (err) {
            console.error('Error loading model:', err);
            const errorCam = new ArcRotateCamera('errorCam', Math.PI / 2, Math.PI / 2.5, 10, Vector3.Zero(), this.scene);
            errorCam.attachControl(canvas, true);
            this.scene.activeCamera = errorCam;
        }

        this.engine.runRenderLoop(() => {
            this.scene.render();
        });
        window.addEventListener('resize', () => {
            this.engine.resize();
        });
        // Once initialization is complete, remove the loading indicator.
        this.loading = false;
    }

    private useCameraNodeAsArc(camNode: TransformNode, targetPos: Vector3) {
        const canvas = this.$refs.bjsCanvas;
        const camPos = camNode.getAbsolutePosition().clone();
        const diff = camPos.subtract(targetPos);
        const radius = diff.length();
        const alpha = Math.atan2(diff.z, diff.x);
        const beta = Math.acos(diff.y / radius);
        const arcCam = new ArcRotateCamera('arcCamera', alpha, beta, radius, targetPos, this.scene);
        arcCam.attachControl(canvas, true);
        // Adjust zoom sensitivity if needed.
        arcCam.wheelPrecision = 100;
        this.scene.activeCamera = arcCam;
    }

    private gatherAllDescendantMeshes(node: Node, out: Set<Mesh>) {
        if (node instanceof Mesh) {
            out.add(node);
        }
        const children = node.getChildren();
        children.forEach(child => this.gatherAllDescendantMeshes(child, out));
    }

    private hideAllExcept(node: Node) {
        const keepSet = new Set<Mesh>();
        this.gatherAllDescendantMeshes(node, keepSet);
        if (keepSet.size === 0) {
            const fallback = this.scene.meshes.filter(m => m.parent === node && m instanceof Mesh) as Mesh[];
            fallback.forEach(m => keepSet.add(m));
        }
        console.log(
            'Keeping visible:',
            Array.from(keepSet).map(m => m.name)
        );
        this.scene.meshes.forEach(m => {
            if (m instanceof Mesh) {
                m.setEnabled(keepSet.has(m));
            }
        });
    }

    private showAllMeshes() {
        this.scene.meshes.forEach(m => {
            if (m instanceof Mesh) {
                m.setEnabled(true);
            }
        });
    }

    public toggleOthers() {
        if (!this.selectedNode) {
            console.warn('No selected equipment node to toggle!');
            return;
        }
        if (this.othersVisible) {
            this.hideAllExcept(this.selectedNode);
        } else {
            this.showAllMeshes();
        }
        this.othersVisible = !this.othersVisible;
    }
}
